import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from "react";
import { Table, message, Modal, Button, Select } from "antd";
import { FocusSDK as focusSDK, isFocusEnv, getBroName, getUserAppId } from "@/utils";
import { GlobalOutlined } from "@/utils/antdIcon";
import debounce from "lodash/debounce";
import Loading from "@/components/Loading";
import styles from "./index.module.less";
import { CurrentType } from "@/components/Modals/NetTable/types";
import UploadImageControler from "@/layout/UploadImage/UploadIamgeControler";
import JSZip from "jszip";
import { v4 } from "uuid";
import { UploadFile } from "antd/lib/upload/interface";
import getVersionBySdk from "@/utils/getVersion";
import { UAParser } from "ua-parser-js";
import { submitFeedback } from "@/api/feedback";
import { ColumnsType } from "antd/es/table";
import sentryManual from "@/utils/sentryManual";
import { reportNetCheckResult } from "@/utils/logAnalytics";

const LOG_CONTENT = "网络检测结果";
const { Option } = Select;
const lostPercent = 10;

const columns: ColumnsType<any> = [
  {
    title: "序号",
    dataIndex: "index",
    align: "center",
    width: 100
  },
  {
    title: "服务名称",
    dataIndex: "serverName",
    align: "center"
  },
  {
    title: "最大延时(毫秒)",
    dataIndex: "maxTime",
    align: "left",
    render: (value: number) => (isNaN(value) ? "-" : value.toFixed(2))
  },
  {
    title: "最小延时(毫秒)",
    dataIndex: "minTime",
    align: "left",
    render: (value: number) => (isNaN(value) ? "-" : value.toFixed(2))
  },
  {
    title: "平均延时(毫秒)",
    dataIndex: "avgTime",
    align: "left",
    render: (value: number) => (isNaN(value) ? "-" : value.toFixed(2))
  },
  {
    title: "丢包率(%)",
    dataIndex: "packetLoss",
    align: "left",
    render: (value: number) =>
      isNaN(value) ? (
        "-"
      ) : value > lostPercent ? (
        <span className={styles["error"]}>{value.toFixed(2)}</span>
      ) : (
        value.toFixed(2)
      )
  }
];

const selectOptions = [
  {
    text: `互联网${!focusSDK?.isPrivateNet() ? "（使用中）" : ""}`,
    value: "public"
  }
  // {
  //   text: `政务外网${focusSDK?.isPrivateNet() ? "（使用中）" : ""}`,
  //   value: "private"
  // }
];

const initValue = focusSDK?.isPrivateNet() ? "private" : "public";
type NetResultType =
  | "CURRENT_NET_OK"
  | "CURRENT_NET_DNS_ERROR"
  | "CURRENT_NET_ERROR"
  | "CURRENT_NET_WEAK"
  | "OTHER_NET_RESULT";

const getPingBytes = () => {
  const deviceInfo = focusSDK?.getDeviceInfo?.() || {};
  const { platform } = deviceInfo || "";
  let defaultBytes = 64;
  if (platform.indexOf("win32") > -1) {
    defaultBytes = 32;
  }

  return defaultBytes;
};

const formatValue = (value: any) => {
  if (isNaN(value)) {
    return "-";
  }
  return value.toFixed(2);
};

const logNetCheckResult = (result: any[]) => {
  const pingBytes = getPingBytes();
  result.forEach((item) => {
    const up3_et = formatValue(item.avgTime);
    let status = 1;
    if (up3_et === "-") {
      // 可解析域名，但ping不通
      if (item.canResolve) {
        status = 2;
      } else {
        // dns解析失败
        status = 3;
      }
    }
    reportNetCheckResult({
      url: item.domain,
      f_s: pingBytes,
      up1_et: formatValue(item.minTime),
      up2_et: formatValue(item.maxTime),
      c_r: formatValue(item.packetLoss),
      up3_et,
      status
    });
  });
};

const NetTable = (props: any) => {
  const {
    visible: netVisible,
    fromType,
    appRuntimeEnv,
    authInfo: authData,
    localConfigData
  } = props;

  const [netList, setNetList] = useState<any[]>([]);
  const [loadingValue, setLoadingValue] = useState(false);
  const [currentType, setCurrentType] = useState<CurrentType>(() => initValue);
  const isQuerying = useRef(false);
  const [checkText, setCheckText] = useState("开始检测");
  const [netResultType, setNetResultType] = useState<NetResultType | "">();
  const [canSendLog, setCanSendLog] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [sendLoading, setSendLoading] = useState(false);
  const [changeLoading, setChangeLoading] = useState(false);
  const [isOtherType, setIsOtherType] = useState(false);
  const [settings, setSettings] = useState(localConfigData?.settings || null);
  const [canChangeNet, setCanChangeNet] = useState({
    private: false,
    public: false
  });
  const [allNetList, setAllNetList] = useState<any>({
    private: {
      list: [],
      canChangeNet: false,
      netResultType: "",
      canSendLog: false
    },
    public: {
      list: [],
      canChangeNet: false,
      netResultType: "",
      canSendLog: false
    }
  });

  useEffect(() => {
    console.log("settings=>", localConfigData?.settings);
    setSettings(localConfigData?.settings);
  }, [localConfigData?.settings]);

  const getCurrentNetResult = useCallback(
    (netResult: any[]) => {
      console.log("耗费时长", Date.now() - currentTime, netResult);
      isQuerying.current = false;
      setLoadingValue(false);
      setCheckText("重新检测");
      setNetList(netResult);
      logNetCheckResult(netResult);
      let flag = true;
      let canChangeNet = true;
      let resultNetType = "public";
      let netResultType: NetResultType | "" = "";
      let canSendLog = false;
      let dnsFlag = true;
      let weakNetFlag = false;

      netResult.forEach((item, index) => {
        if (index === 0 && item.domain.indexOf("beijing.egov") > -1) {
          resultNetType = "private";
        }
        // DNS无法解析
        if (isNaN(item.packetLoss)) {
          dnsFlag = false;
        }
        if (item.packetLoss > lostPercent || isNaN(item.packetLoss)) {
          // 网关接口，是否可发送日志
          if (item.id === "1" && fromType !== "entry") {
            setCanSendLog(false);
          }
          flag = false;
          canChangeNet = false;
        } else if (item.packetLoss <= lostPercent) {
          // 弱网
          weakNetFlag = true;
          if (item.id === "1" && fromType !== "entry") {
            canSendLog = true;
            setCanSendLog(true);
          }
        }
      });
      setCanChangeNet((prevValue: any) => {
        return {
          ...prevValue,
          ...{ [resultNetType]: canChangeNet }
        };
      });
      if (flag) {
        netResultType = "CURRENT_NET_OK";
      } else if (!dnsFlag) {
        netResultType = "CURRENT_NET_DNS_ERROR";
      } else if (weakNetFlag) {
        netResultType = "CURRENT_NET_WEAK";
      } else {
        netResultType = "CURRENT_NET_ERROR";
      }
      setNetResultType(netResultType);
      setAllNetList((preList: any) => {
        return {
          ...preList,
          ...{
            [resultNetType]: {
              list: netResult,
              canChangeNet,
              netResultType,
              canSendLog
            }
          }
        };
      });
    },
    [currentTime, fromType]
  );

  const checkCurrentNet = useCallback(() => {
    if (!isQuerying.current) {
      setLoadingValue(true);
      setCanSendLog(false);
      setCurrentTime(Date.now());
      setNetResultType("");
      isQuerying.current = true;
      setCheckText("检测中...");
      setTimeout(() => {
        focusSDK.checkCurrentNet({
          fromType: fromType || "",
          currentType
        });
      });
    }
  }, [currentType, fromType]);

  const retryCheck = useCallback(() => {
    checkCurrentNet();
  }, [checkCurrentNet]);

  const uploadController = useMemo(() => {
    return new UploadImageControler(appRuntimeEnv);
  }, [appRuntimeEnv]);

  const getLogFiles = () => {
    return ["main.log", "main.old.log", "uncaughtException-latest.log"]
      .map((item) => {
        const file = focusSDK.getLogFile(item);
        if (!file) {
          return null;
        }
        return {
          ...file,
          name: item
        };
      })
      .filter((item) => !!item);
  };

  const initFileData = (data: any): UploadFile => {
    return {
      uid: data.uploadId || v4(),
      name: data.name,
      status: "done",
      url: data.downloadUrl,
      size: data.size,
      type: data.type
    };
  };

  const getLogsUrl = async () => {
    const zip = new JSZip();
    getLogFiles().forEach((item: any) => {
      zip.file(item.name, item.blob, { binary: true });
    });
    const content = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE", // STORE：默认不压缩 DEFLATE：需要压缩
      compressionOptions: {
        level: 9 // 压缩等级1~9 1压缩速度最快，9最优压缩方式
      }
    });
    const userName = authData.realName;
    const currentDate = new Date().getTime();
    const logFile = new File([content], `logs-${userName}-${currentDate}.zip`);
    try {
      let state = await uploadController.asyncAddFile(logFile);
      return [initFileData(state)].map((i: UploadFile) => i.url);
    } catch (e) {
      return [];
    }
  };

  const ver = useMemo(() => getBroName(), []);

  const sendLog = async () => {
    setSendLoading(true);
    const { name } = focusSDK.getApplicationInfo();
    const appVersion = isFocusEnv()
      ? getVersionBySdk(focusSDK)
      : appRuntimeEnv.registryConfig.pkgVersion.split("-")[0];
    const appName = `${name} ${isFocusEnv() ? "desktop" : "web"}`;
    let uaParser = new UAParser();
    let os = uaParser.getOS();
    let system_version = "1.0.0";
    if (os.name && os.version) {
      system_version = `${os.name}-${os.version}`;
    }
    const params = {
      expression: "",
      content: `${LOG_CONTENT}-${authData.realName || authData.teamUserInfo.realName}`,
      type: "1",
      system_name: "focus-desktop",
      system_version: system_version,
      device_model: ver,
      name: authData.realName || authData.teamUserInfo.realName,
      mobile: authData.teamUserInfo.mobile,
      company: authData.teamUserInfo.teamFullName || authData.teamUserInfo.teamShortName || "",
      logs: await getLogsUrl()
    };
    const app = getUserAppId();
    return submitFeedback({
      params: { ...params, appName, app, appVersion, system_version }
    }).then((result) => {
      setSendLoading(false);
      if (!result) {
        message.success("提交成功");
      } else {
        message.info("提交失败");
      }
    });
  };

  useEffect(() => {
    focusSDK.on("checkCurrentNet:reply", getCurrentNetResult);
    return () => {
      focusSDK.off("checkCurrentNet:reply", getCurrentNetResult);
    };
  }, [getCurrentNetResult, checkCurrentNet]);

  useEffect(() => {
    if (netVisible) {
      setNetList([]);
      setLoadingValue(false);
      isQuerying.current = false;
      setCheckText("开始检测");
      setNetResultType("");
      setCanSendLog(false);
      setSendLoading(false);
      setCanChangeNet({
        private: false,
        public: false
      });
      setCurrentType(focusSDK.isPrivateNet() ? "private" : "public");
      setIsOtherType(false);
    }
  }, [netVisible]);

  const changeCurrentType = (val: CurrentType) => {
    const { list, canChangeNet, netResultType, canSendLog } = allNetList[val];
    setNetList(list);
    setCanChangeNet((prevValue: any) => {
      return {
        ...prevValue,
        ...{ [val]: canChangeNet }
      };
    });
    setNetResultType(netResultType);
    setCanSendLog(canSendLog);
    setCurrentType(val);
    setIsOtherType(val !== initValue);
  };

  const changeHandle = useCallback(
    (valueParam: CurrentType) => {
      if (!settings) return;
      let type = "egovFirst";
      let valueBk = settings[type];

      const value = valueParam === "private";
      settings[type] = value;
      setSettings(settings);
      sentryManual.captureMessage(
        `网络检测后切换至${value ? "政务外网" : "互联网"}`,
        "net-change-manual"
      );
      Modal.confirm({
        title: "提示",
        content: `是否切换至${value ? "政务外网" : "互联网"}，确认之后应用将会重启。`,
        zIndex: 3000,
        onOk: () => {
          focusSDK.setConfigData(`settings|${type}`, value);
          focusSDK.setConfigData(`settings|netSettingChanged`, true);
          setChangeLoading(false);
          focusSDK.on("configDataChange", focusSDK.relaunchApp);
        },
        onCancel: () => {
          settings[type] = valueBk;
          setSettings(settings);
          setChangeLoading(false);
        },
        okText: "确定",
        cancelText: "取消"
      });
    },
    [settings]
  );

  const changeNetType = useCallback(async () => {
    try {
      setChangeLoading(true);
      message.loading("处理中...", 0);
      message.destroy();
      changeHandle(currentType);
    } catch (e) {
      message.destroy();
    }
  }, [currentType, changeHandle]);

  return (
    <Modal
      title={
        <div className={styles["title-container"]}>
          <GlobalOutlined />
          <div className={styles["modal-title"]}>网络检测</div>
        </div>
      }
      bodyStyle={{
        padding: "0 0 8px",
        display: "flex",
        flexDirection: "column"
      }}
      width={800}
      closable={true}
      visible={netVisible}
      maskClosable={false}
      wrapClassName="net-table-modal"
      keyboard={false}
      zIndex={2000}
      footer={null}
      onCancel={() => focusSDK.emit("hideNetTable")}
    >
      <div className={styles["operation-container"]}>
        <div className={styles["table-operation"]}>
          <div className="current-net-tag">
            <span>当前检测网络：</span>
            <Select
              className={styles["select-net"]}
              dropdownClassName={styles["dropdown-net"]}
              value={currentType}
              disabled={loadingValue}
              onChange={changeCurrentType}
            >
              {selectOptions.map((item) => (
                <Option key={item.value} value={item.value}>
                  {item.text}
                </Option>
              ))}
            </Select>
          </div>
          <div className={styles["btn-container"]}>
            {canSendLog && (
              <Button
                className={styles["send-btn"]}
                type="primary"
                loading={sendLoading}
                disabled={sendLoading}
                onClick={debounce(sendLog, 300)}
              >
                发送日志
              </Button>
            )}
            <Button
              className={styles["check-btn"]}
              type="primary"
              disabled={isQuerying.current}
              onClick={debounce(retryCheck, 300)}
              clstag="pageclick|keycount|xtbg_pc_network_detection_1687846552270|1"
            >
              {checkText}
            </Button>
            {/* <Button
              className={styles["check-btn"]}
              type="primary"
              disabled={changeLoading || !canChangeNet[currentType] || !isOtherType}
              onClick={debounce(changeNetType, 300)}
              clstag="pageclick|keycount|xtbg_pc_network_detection_1687846552270|2"
            >
              切换至该网络
            </Button> */}
          </div>
        </div>
        <div className={styles["table-result"]}>
          <span>检测结果：</span>
          {netResultType === "CURRENT_NET_OK" && (
            <span className={styles["ok"]}>
              {currentType === "public" ? "互联网连接正常" : "政务外网连接正常"}
              {isOtherType ? "，您可以切换至该网络。" : "。"}
            </span>
          )}
          {netResultType === "CURRENT_NET_DNS_ERROR" && (
            <span className={styles["error"]}>
              域名解析异常，请检查您的DNS设置或联系所在单位网络管理员解决。
            </span>
          )}
          {netResultType === "CURRENT_NET_WEAK" && (
            <span className={styles["warn"]}>
              {currentType === "public" ? "互联网" : "政务外网"}
              不稳定，可能会影响您的访问速度，但仍可继续使用。
            </span>
          )}
          {netResultType === "CURRENT_NET_ERROR" && (
            <span className={styles["error"]}>
              {currentType === "public" ? "互联网" : "政务外网"}
              连接异常，
              {isOtherType
                ? "无法使用。"
                : "如有疑问请联系所在单位网络管理员或拨打客服电话：400-615-1212。"}
            </span>
          )}
        </div>
      </div>
      <Table
        rowClassName={styles["net-list-table-row"]}
        className={styles["net-list-table"]}
        columns={columns}
        dataSource={netList}
        loading={
          loadingValue
            ? {
                indicator: (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      top: 0,
                      left: 0
                    }}
                  >
                    <Loading />
                  </div>
                )
              }
            : false
        }
        rowKey="id"
        pagination={false}
        scroll={{ y: 300 }}
      />
    </Modal>
  );
};

export default NetTable;
